import posthog from 'posthog-js';
import { iniciarPostHog, posthogAtivo } from '@titularis/analytics';

/**
 * Produto: PostHog (decisão D-9 do plano - analytics de produto e observabilidade
 * no mesmo lugar, em vez de uma ferramenta só para erro).
 *
 * Sem `VITE_POSTHOG_KEY` nada é iniciado e todo evento vira no-op: em
 * desenvolvimento e nos previews não queremos sujar os números, e o app precisa
 * funcionar igual com ou sem a chave.
 */
const chave = import.meta.env.VITE_POSTHOG_KEY;
const host = import.meta.env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com';

export function iniciarAnalytics(): void {
  /*
   * `pageview: 'manual'` preserva a decisão que este arquivo já tinha: as telas
   * são de rota do lado do cliente, e quem diz que uma tela aconteceu é o
   * `registrarTela` abaixo. Sem este argumento o pacote assume o modo
   * automático - que é o certo para a landing e o errado aqui, porque as duas
   * capturas conviveriam e toda tela do app entraria duas vezes.
   */
  /*
   * **O app grava desde 22/09/2026.** Até lá esta linha dizia `false`, porque
   * gravar a tela daqui era despejar o produto no PostHog sem a marca do
   * leitor que o G-WATERMARK põe em tudo o que se entrega.
   *
   * Virou `true` no começo do GTM. Com poucos alunos, o agregado não diz nada
   * e ver cada sessão diz quase tudo: onde a pessoa travou, o que ela não
   * achou, em que tela desistiu da compra. A condição foi tirar o material da
   * gravação por construção, e não por cuidado: todo `ConteudoRico` protegido
   * vira um retângulo vazio no replay, e o corpo das requisições não é gravado
   * (ver `ATRIBUTO_DE_MATERIAL_AUTORAL` no pacote).
   *
   * O token de `/nova-senha` também não pode chegar aqui, e quem cuida disso é
   * o `guardarTokenDeRedefinicao`, que o `main.tsx` chama antes desta função.
   */
  iniciarPostHog({ chave, host, pageview: 'manual', gravarSessao: true });
}

export function identificar(usuario: { id: string; email: string }): void {
  if (!posthogAtivo()) return;
  posthog.identify(usuario.id, { email: usuario.email });
}

export function encerrarSessaoDeAnalytics(): void {
  if (!posthogAtivo()) return;
  posthog.reset();
}

/**
 * Os parâmetros que podem acompanhar a tela até o PostHog.
 *
 * **Lista de permissão, e não de bloqueio.** O `registrarTela` montava
 * `origem + caminho` e descartava a busca inteira, então nenhuma UTM chegava ao
 * PostHog - o relatório de aquisição dele ficava cego para todo link que
 * apontasse direto para o app. Passar a busca crua consertaria isso e abriria
 * outro buraco: `/nova-senha?token=...` mandaria o **token de redefinição de
 * senha** para dentro do analytics, onde ele fica gravado e legível.
 *
 * Com lista de permissão, parâmetro novo nasce de fora por padrão. É a ordem
 * certa: esquecer de incluir uma UTM custa um relatório, esquecer de excluir um
 * segredo custa uma conta.
 */
const PARAMETROS_DE_CAMPANHA = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
  'gclid', 'fbclid', 'igshid', 'ttclid', 'msclkid', 'wbraid', 'gbraid',
];

export function registrarTela(caminho: string): void {
  if (!posthogAtivo()) return;

  const url = new URL(caminho, window.location.origin);
  const permitidos = new URLSearchParams();
  for (const chave of PARAMETROS_DE_CAMPANHA) {
    const valor = new URLSearchParams(window.location.search).get(chave);
    if (valor) permitidos.set(chave, valor);
  }

  const busca = permitidos.toString();
  posthog.capture('$pageview', {
    $current_url: `${url.origin}${url.pathname}${busca ? `?${busca}` : ''}`,
  });
}

/** Eventos do produto. Nomes em inglês, como o resto do código. */
export type EventoDeProduto =
  | { nome: 'question_answered'; questionId: string; module: string; isCorrect: boolean }
  | { nome: 'commentary_opened'; questionId: string; module: string }
  /*
   * O `produto` entrou em 05/09/2026, junto do paywall que virou vitrine: o
   * modal passou a oferecer um produto concreto, escolhido pelo `grants` do
   * catálogo, e sem registrá-lo não dá para saber qual oferta foi mostrada -
   * só que alguma foi.
   */
  | { nome: 'paywall_shown'; motivo: string; origem: 'answer' | 'commentary'; produto: string | null }
  /*
   * O funil da venda: paywall_shown -> checkout_started -> purchase_confirmed.
   * A forma de pagamento vai junto porque a pergunta comercial que sobra é
   * quantos escolhem Pix quando há desconto. A duração e a recorrência separam
   * os planos sem gravar o rótulo derivável no evento: sem elas, mensal,
   * semestral e anual cairiam no mesmo passo do funil.
   */
  /*
   * A faixa do topo entrou no funil em 02/09/2026, antes do `paywall_shown`:
   * ela fala de plano enquanto ainda há cota, e o paywall só fala depois que
   * ela acaba. Saber qual dos dois traz mais gente para a vitrine é o que
   * decide se a faixa fica.
   */
  | { nome: 'plan_banner_clicked'; restante: number }
  /*
   * `origem` diz de qual superfície a compra partiu. Sem ela, o funil mede que
   * alguém comprou e não de onde - e a pergunta que decide se o modal e as
   * boas-vindas ficam é exatamente essa.
   */
  | {
      nome: 'checkout_started';
      produto: string;
      forma: 'card' | 'pix';
      meses: number;
      recorrente: boolean;
      origem: OrigemDaCompra;
    }
  | { nome: 'purchase_confirmed'; pedido: string | null }
  /*
   * As boas-vindas (05/09/2026). O passo vai junto porque abandono no meio é
   * informação diferente de nunca ter aberto: um diz que o onboarding é longo,
   * o outro que ele não aparece.
   *
   * `dispensas` diz **em qual das três exibições** a pessoa estava. É o que
   * responde se a segunda chance vale a pena: se ninguém conclui na segunda nem
   * na terceira, o teto pode voltar a ser um.
   *
   * **`foco` não é mais a resposta das boas-vindas** (10/09/2026): a pergunta
   * da etapa saiu do fluxo e ficou em Configurações, então o campo passou a
   * carregar a preferência **já gravada** - nula em conta nova, preenchida em
   * quem voltou. Ele fica porque os eventos de setembro o carregam com o outro
   * sentido, e tirá-lo quebraria a leitura do funil de antes da mudança; para
   * medir a etapa declarada hoje, a fonte é a coluna, não este evento.
   */
  | { nome: 'onboarding_shown' }
  | { nome: 'onboarding_step'; passo: number }
  | { nome: 'onboarding_finished'; foco: string | null; concluiu: boolean; dispensas: number }
  | { nome: 'error_reported'; questionId: string; kind: string }
  /**
   * O comentário de um artigo, aberto na Legislação Comentada.
   *
   * **A tela inteira era muda até 18/09/2026.** São 665 artigos comentados em
   * 65 leis, cada Legislação Comentada é um produto à venda, e o único sinal que
   * saía de lá era o `paywall_shown` do bloqueio - de modo que "quantos leram
   * antes de esbarrar no cadeado" não tinha resposta, e é ela que diz se a
   * degustação convence ou só irrita.
   *
   * **Mede a abertura avulsa, que é o caminho da degustação**, e não a leitura
   * de quem tem direito: para esse o comentário já vem embutido na página, sem
   * clique nenhum a observar. É justamente o recorte que interessa, porque o
   * cadeado só aparece nesse caminho - `article_opened` por pessoa antes do
   * `paywall_shown` é a conta que se quer fazer.
   *
   * `lei` e `endereco` em vez do id do artigo: o id não quer dizer nada num
   * relatório, e a pergunta é sempre por lei - qual delas puxa leitura.
   */
  | { nome: 'article_opened'; lei: string; endereco: string }
  /**
   * A gravação da origem falhou.
   *
   * Existe porque a ausência dela custou cinco dias: a chamada era disparada com
   * `.catch(() => undefined)`, a rota respondia 422 para todo mundo e nada no
   * mundo dizia isso. O `motivo` é `status:code` da API, que é o que separa
   * contrato quebrado de sessão perdida e de rede.
   */
  | { nome: 'attribution_failed'; motivo: string };

/**
 * De onde a compra partiu.
 *
 * A vitrine (`planos`) saiu em 05/09/2026, quando a oferta virou um modal só:
 * o valor fica no tipo porque eventos antigos do PostHog o carregam, e tirá-lo
 * quebraria a leitura do funil de antes da mudança.
 *
 * O que sobra é a pergunta comercial da rodada: **o modal vende mais quando
 * interrompe (`paywall`) ou quando é procurado (`oferta`)?**
 */
export type OrigemDaCompra = 'planos' | 'paywall' | 'oferta' | 'boas-vindas';

export function registrarEvento(evento: EventoDeProduto): void {
  if (!posthogAtivo()) return;
  const { nome, ...propriedades } = evento;
  posthog.capture(nome, propriedades);
}
